import { useState, useEffect } from 'react'
import { Volume2, VolumeX, Play, Pause, SkipForward, X, Mic } from 'lucide-react'
import voiceAssistant from '../../utils/voiceAssistant'

const TOUR_STEPS = [
    {
        id: 'welcome',
        text: "Welcome to InkSpot! I'm your AI guide. Let me show you how InkSpot can transform your writing.",
        highlight: null,
    },
    {
        id: 'problem',
        text: "Every writer struggles with clichés and overused phrases. They make your writing feel stale and unoriginal.",
        highlight: null,
    },
    {
        id: 'solution',
        text: "InkSpot uses advanced AI to detect clichés in real-time. Just paste your text, and I'll find every tired expression.",
        highlight: '#features',
    },
    {
        id: 'suggestions',
        text: "But I don't just point out problems. I suggest fresh, creative alternatives that maintain your meaning.",
        highlight: '#features',
    },
    {
        id: 'pro',
        text: "Pro users get unlimited AI analysis, weekly writing reports, and priority support. All for just seven dollars a month.",
        highlight: '#pricing',
    },
    {
        id: 'cta',
        text: "Ready to write better? Sign up now and start eliminating clichés from your writing today!",
        highlight: null,
    },
]

export default function VoiceTour() {
    const [isOpen, setIsOpen] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [isMuted, setIsMuted] = useState(false)
    const [isAvailable, setIsAvailable] = useState(true)

    useEffect(() => {
        // Check if speech synthesis is available
        setIsAvailable(voiceAssistant.isAvailable())

        // Load voices (chrome needs this)
        if (voiceAssistant.isAvailable()) {
            voiceAssistant.getVoices()
        }
    }, [])

    useEffect(() => {
        voiceAssistant.onEnd(() => {
            // Auto-advance to next step
            if (currentStep < TOUR_STEPS.length - 1 && isPlaying && !isMuted) {
                setTimeout(() => {
                    setCurrentStep(prev => prev + 1)
                }, 800)
            } else if (currentStep >= TOUR_STEPS.length - 1) {
                setIsPlaying(false)
            }
        })
    }, [currentStep, isPlaying, isMuted])

    useEffect(() => {
        // Speak current step when it changes
        if (isPlaying && !isMuted && isOpen) {
            const step = TOUR_STEPS[currentStep]
            voiceAssistant.speak(step.text)

            // Scroll to highlight section if specified
            if (step.highlight) {
                const element = document.querySelector(step.highlight)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            }
        }
    }, [currentStep, isPlaying, isMuted, isOpen])

    const startTour = () => {
        setIsOpen(true)
        setCurrentStep(0)
        setIsPlaying(true)
    }

    const togglePlay = () => {
        if (isPlaying) {
            voiceAssistant.pause()
            setIsPlaying(false)
        } else {
            // Just speak the current step text (resume is handled internally)
            voiceAssistant.speak(TOUR_STEPS[currentStep].text)
            setIsPlaying(true)
        }
    }

    const skipStep = () => {
        voiceAssistant.stop()
        if (currentStep < TOUR_STEPS.length - 1) {
            setCurrentStep(prev => prev + 1)
        } else {
            closeTour()
        }
    }

    const closeTour = () => {
        voiceAssistant.stop()
        setIsOpen(false)
        setIsPlaying(false)
        setCurrentStep(0)
    }

    const toggleMute = () => {
        setIsMuted(!isMuted)
        if (!isMuted) {
            voiceAssistant.stop()
        }
    }

    if (!isAvailable) {
        return null
    }

    return (
        <>
            {/* Tour Trigger Button */}
            {!isOpen && (
                <button
                    onClick={startTour}
                    className="fixed bottom-40 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                >
                    <Mic className="w-5 h-5" />
                    <span className="font-medium">Take a Tour</span>
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                </button>
            )}

            {/* Tour Player */}
            {isOpen && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4">
                    <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
                        {/* Progress bar */}
                        <div className="h-1 bg-slate-800">
                            <div
                                className="h-full bg-gradient-to-r from-cyan-500 to-purple-600 transition-all duration-500"
                                style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
                            />
                        </div>

                        <div className="p-4">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 flex items-center justify-center">
                                        <Mic className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-white/70">
                                        Step {currentStep + 1} of {TOUR_STEPS.length}
                                    </span>
                                </div>
                                <button
                                    onClick={closeTour}
                                    className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <p className="text-white text-sm leading-relaxed mb-4 min-h-[48px]">
                                {TOUR_STEPS[currentStep].text}
                            </p>

                            {/* Controls */}
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={toggleMute}
                                    className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </button>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={togglePlay}
                                        className="p-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-full hover:opacity-90 transition-opacity"
                                    >
                                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                                    </button>
                                    <button
                                        onClick={skipStep}
                                        className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                    >
                                        <SkipForward className="w-5 h-5" />
                                    </button>
                                </div>

                                <a
                                    href="/signup"
                                    className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                                >
                                    Sign Up →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
